import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import Sortable from 'react-sortablejs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripLines } from '@fortawesome/pro-regular-svg-icons/faGripLines'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import { Field, FieldArray } from 'formik'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce-promise'

import { GET_LIST, useClient } from '@peracto/client'

import { Form, Group, Input } from '@peracto/peracto-ui'

import { SortableRow } from '@peracto/peracto-ui'

export const MODE_ADD = 'add'
export const MODE_EDIT = 'edit'

const AttributeSetForm = ({ mode = MODE_EDIT, attributeSetGroups = [], values, ...props }) => {
    const { client } = useClient()
    const [groups, setGroups] = useState([])

    const fetchGroups = async (inputValue, fieldVal) => {
        const { data } = await client(GET_LIST, 'attribute-groups', {
            id: 'attribute-groups',
            name: inputValue,
        })

        setGroups(data)

        const selectedIds = fieldVal
            ? fieldVal.map(value => {
                  return value.originId ? value.originId : value.id
              })
            : []

        const values = data
            .filter(attr => !selectedIds.includes(attr.originId))
            .map(attr => ({
                label: attr.name,
                value: attr.id,
            }))

        return values
    }

    const debouncedFetchGroups = debounce(fetchGroups, 200)

    const attributeSetValues = {
        name: '',
        ...values,
        groups: attributeSetGroups,
    }

    return (
        <Form autoComplete="off" {...props} values={attributeSetValues}>
            <div data-testid="attribute-set-properties">
                <Group
                    key="attribute-set-properties"
                    id="attribute-set-properties"
                    name="Attribute Set Properties"
                >
                    <Input
                        name="name"
                        label="Name"
                        required
                        autoComplete="label"
                        testId="attribute-set-name"
                    />
                </Group>
            </div>

            <div data-testid="set-groups">
                <Group key="set-groups" id="set-groups" name="Groups">
                    <Field name="groups">
                        {({ field, form }) => (
                            <>
                                <FieldArray name={field.name}>
                                    {() => (
                                        <table className="table table-sm">
                                            <colgroup>
                                                <col width="10%" />
                                                <col width="75%" />
                                                <col width="15%" />
                                            </colgroup>

                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Name</th>
                                                    <th className="!text-right">Remove</th>
                                                </tr>
                                            </thead>

                                            <Sortable
                                                tag="tbody"
                                                onChange={(order, sortable, evt) => {
                                                    const values = [...field.value]
                                                    const movedItem = values[evt.oldIndex]
                                                    const to = evt.newIndex
                                                    values.splice(evt.oldIndex, 1)
                                                    values.splice(to, 0, movedItem)
                                                    form.setFieldValue('groups', values)
                                                }}
                                                options={{
                                                    animation: 100,
                                                }}
                                            >
                                                {field.value.length > 0 &&
                                                    field.value.map((item, index) => (
                                                        <SortableRow
                                                            key={item.id}
                                                            data-id={item.id}
                                                            data-testid={`group-row-${index}`}
                                                        >
                                                            <td>
                                                                <FontAwesomeIcon
                                                                    icon={faGripLines}
                                                                    data-testid={`group-${index}-slider_handle`}
                                                                />
                                                            </td>
                                                            <td data-testid={`group-${index}-name`}>
                                                                {item.name}
                                                            </td>
                                                            <td className="!text-right">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    data-testid={`remove-group-${index}`}
                                                                    onClick={() => {
                                                                        const values = [
                                                                            ...field.value,
                                                                        ]
                                                                        values.splice(index, 1)
                                                                        form.setFieldValue(
                                                                            'groups',
                                                                            [...values]
                                                                        )
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faTrash}
                                                                    />
                                                                </button>
                                                            </td>
                                                        </SortableRow>
                                                    ))}
                                            </Sortable>
                                        </table>
                                    )}
                                </FieldArray>
                                <div className="row">
                                    <div className="col-12">
                                        <div data-testid="attribute-group-search">
                                            <AsyncSelect
                                                classNamePrefix="list"
                                                loadOptions={input =>
                                                    debouncedFetchGroups(input, field.value)
                                                }
                                                onChange={option => {
                                                    const selectedValue = groups.filter(
                                                        attr => attr.id == option.value
                                                    )
                                                    const fieldValues = [...field.value]
                                                    fieldValues.push(...selectedValue)
                                                    form.setFieldValue('groups', [...fieldValues])
                                                }}
                                                value={null}
                                                placeholder="Search for Attribute Groups to add..."
                                                noOptionsMessage={({ inputValue }) => {
                                                    if (inputValue.length > 0) {
                                                        return `No results found for '${inputValue}'.`
                                                    } else {
                                                        return 'Enter text to begin searching.'
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="!flex !justify-center !mt-2">
                                            <Link
                                                to="/attribute-groups/add"
                                                className="btn btn-link"
                                                data-testid="create-attribute-group"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPlusCircle}
                                                    className="!mr-2"
                                                />
                                                Create Group
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </Field>
                </Group>
            </div>
        </Form>
    )
}

AttributeSetForm.displayName = 'AttributeSetForm'

AttributeSetForm.propTypes = {
    values: PropTypes.object,
    mode: PropTypes.oneOf([MODE_ADD, MODE_EDIT]),
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default AttributeSetForm
